/**
 * Клиент сводки руководителя и реестра обязательств (E12). Типы согласованы с сервером ПО
 * ЗНАЧЕНИЯМ (клиент и api типы напрямую не делят) — формы из apps/api/src/summary/*.
 *
 * Ни одной цифры здесь не считается: и суммы, и статусы, и порядок строк приходят готовыми.
 * Так экран директора и карточка договора не могут разойтись — считает их один и тот же код
 * на сервере (E12, требование «один путь расчёта»).
 */

import type { ApiResult } from "./contracts";
import type { Obligation, ObligationStatus, ObligationType } from "./obligations";
import type { Role } from "./roles";

/** Строка реестра/сводки. Начисленных санкций в ней НЕТ — они содержание метрик сводки. */
export interface SummaryRow {
  readonly obligationId: string;
  readonly contractId: string;
  /** Номер договора; пустой в базе приходит как «б/н». */
  readonly contractNumber: string;
  readonly contractDate: string;
  readonly counterpartyId: string;
  readonly counterpartyName: string;
  readonly type: ObligationType;
  readonly typeLabel: string;
  readonly descr: string;
  readonly debtor: Obligation["debtor"];
  readonly amount: string | null;
  readonly remaining: string | null;
  readonly responsible: Role | null;
  readonly due: string | null;
  readonly status: ObligationStatus;
  readonly statusLabel: string;
  readonly overdue: number;
  readonly daysLeft: number | null;
  readonly escalated: boolean;
}

/** Строка реестра контроля исковой давности (ТЗ 4.2.5). */
export interface LimitationRow extends SummaryRow {
  readonly limitDate: string | null;
  readonly limitDaysLeft: number | null;
  readonly limitExpired: boolean;
  readonly hasActiveClaim: boolean;
}

/** Итоги по стороне-должнику — те же три числа, что в карточке договора. */
export interface SummaryTotals {
  /** Обязательств С НАЧИСЛЕННЫМИ САНКЦИЯМИ (сюда входит и погашенное с опозданием). */
  readonly count: number;
  readonly debt: string;
  readonly sanctions: string;
}

export interface SummarySide {
  readonly totals: SummaryTotals;
  /** Сколько обязательств этой стороны просрочено СЕЙЧАС — цифра, отличная от `totals.count`. */
  readonly overdueNow: number;
}

export interface SummaryMetrics {
  readonly onControl: { readonly total: number; readonly awaiting: number };
  readonly claimsToThem: SummarySide;
  readonly exposure: SummarySide;
  readonly horizon: { readonly days7: number; readonly days14: number };
  readonly escalated: number;
}

export interface Summary {
  /** «Сегодня» организации (Асия/Омск), на которое собран весь экран. */
  readonly today: string;
  readonly metrics: SummaryMetrics;
  /** Показанные эскалации — сервер отдаёт не весь перечень, а начало по критичности. */
  readonly escalations: SummaryRow[];
  /** Сколько эскалаций показано; всего их — `metrics.escalated`. */
  readonly escalationsShown: number;
  /** Реестр давности приходит целиком: полного реестра с фильтрами в системе нет. */
  readonly limitation: LimitationRow[];
}

/** Строка с начисленными санкциями — таблицы «наши требования» и «наши риски». */
export interface SanctionRow extends SummaryRow {
  readonly dueUsed: string | null;
  /** Остаток долга — та же величина, что попала в итоги над таблицей. */
  readonly debt: string | null;
  readonly sanctions: string;
  readonly primary: "penalty" | "interest395";
}

/** Претензия, по которой досудебный порядок можно считать соблюдённым (ТЗ 4.2.5). */
export interface ClaimToCourtRow {
  readonly obligationId: string;
  readonly contractId: string;
  readonly contractNumber: string;
  readonly counterpartyName: string;
  readonly descr: string;
  readonly claimId: string;
  readonly outNumber: string;
  readonly sentDate: string;
  readonly answerDue: string;
  readonly expired: boolean;
  readonly result: string | null;
}

/** Правовой свод по всем действующим договорам — раздел «Претензии и риски». */
export interface LegalRegistry {
  readonly today: string;
  readonly claimsToThem: SummaryTotals;
  readonly exposure: SummaryTotals;
  readonly sanctions: SanctionRow[];
  readonly claimsToCourt: ClaimToCourtRow[];
  readonly limitation: LimitationRow[];
}

/** Страница реестра обязательств. */
export interface RegistryPage {
  readonly today: string;
  readonly rows: SummaryRow[];
  /** Сколько строк подошло под фильтры ВСЕГО (не на этой странице). */
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

/** Фильтры реестра — ровно перечень ТЗ 4.2.6. Пустые значения в запрос не уходят. */
export interface RegistryFilters {
  readonly status?: ObligationStatus | "";
  readonly type?: ObligationType | "";
  readonly debtor?: Obligation["debtor"] | "";
  readonly responsible?: Role | "";
  readonly counterpartyId?: string;
  readonly contractId?: string;
}

/**
 * Общая загрузка JSON. Причины отказа — из того же словаря `ApiResult`, что у остальных клиентов:
 * страница различает «сессия истекла» (уводит на вход), «нет прав» и «сеть» (даёт повторить).
 * Текст берём с сервера, когда он есть, — там объяснение на языке пользователя.
 */
async function getJson<T>(url: string, whatFailed: string): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }

  if (res.ok) {
    const body = (await res.json().catch(() => null)) as T | null;
    if (body === null) {
      return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
    }
    return { ok: true, value: body };
  }

  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  const message = body?.message;
  if (res.status === 403) {
    return { ok: false, reason: "forbidden", message: message ?? "Недостаточно прав." };
  }
  if (res.status === 400) {
    return { ok: false, reason: "validation", message: message ?? "Проверьте параметры отбора." };
  }
  return { ok: false, reason: "network", message: message ?? whatFailed };
}

/** Сводка руководителя (GET /api/summary). Право `summary:view` проверяет сервер. */
export function fetchSummary(): Promise<ApiResult<Summary>> {
  return getJson<Summary>("/api/summary", "Не удалось загрузить сводку.");
}

/**
 * Правовой свод по всем действующим договорам (GET /api/summary/legal) — ОДНИМ запросом.
 * Раньше раздел «Претензии и риски» обходил договоры по одному и складывал деньги в браузере:
 * на проектном объёме это 500 последовательных обращений и второй путь расчёта (Р-41).
 */
export function fetchLegalRegistry(): Promise<ApiResult<LegalRegistry>> {
  return getJson<LegalRegistry>("/api/summary/legal", "Не удалось загрузить претензии и риски.");
}

/** Страница реестра обязательств (GET /api/registry/obligations). */
export function fetchObligationRegistry(
  filters: RegistryFilters,
  page: number,
  pageSize: number,
): Promise<ApiResult<RegistryPage>> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (typeof value === "string" && value !== "") params.set(key, value);
  }
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  return getJson<RegistryPage>(
    `/api/registry/obligations?${params.toString()}`,
    "Не удалось загрузить реестр обязательств.",
  );
}

/**
 * Ссылка на обязательство: карточка его договора с ПОДСВЕТКОЙ нужной строки. Отдельного экрана
 * обязательства нет (E7 держит их блоком в карточке), поэтому переход из реестров и из центра
 * уведомлений ведёт сюда — и сразу показывает, о какой именно строке речь.
 */
export function obligationHref(row: { contractId: string; obligationId: string }): string {
  return `/contracts/${row.contractId}?obl=${encodeURIComponent(row.obligationId)}`;
}
